import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";

import { fetchDeviceLookup } from "./devicesApi";
import { addRmaIntake, updateRmaIntake, type QualityRmaDetailResponse } from "./rmaApi";

const MODAL_HELP = [
  "Scan the unit serial and the model fills from the device Receiving checked in.",
  "AA voltage and C voltage are numbers, such as 3.65.",
  "Visual inspection is a longer note.",
].join(" ");

const EMPTY_FORM = {
  unitSerial: "",
  unitModel: "",
  aaVoltage: "",
  cVoltage: "",
  boardRevision: "",
  visualInspection: "",
};

const VOLTAGE_PATTERN = /^\d+(\.\d{1,3})?$/;

function voltageValue(raw: string, label: string): string | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (!VOLTAGE_PATTERN.test(trimmed)) {
    throw new Error(`${label} must be a number such as 3.65.`);
  }
  return trimmed;
}

export function QualityRmaIntakeModal({
  open,
  rmaId,
  intake,
  seedSerial,
  seedModel,
  onClose,
  onLogged,
}: {
  open: boolean;
  rmaId: number;
  intake?: QualityRmaDetailResponse["intake"][number] | null;
  seedSerial?: string | null;
  seedModel?: string | null;
  onClose: () => void;
  onLogged: (detail: QualityRmaDetailResponse) => void;
}): JSX.Element | null {
  const [form, setForm] = useState(EMPTY_FORM);
  const [lookupSerial, setLookupSerial] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setLookupSerial("");
      setErrorMessage(null);
      return;
    }
    setForm({
      unitSerial: intake?.unitSerial ?? seedSerial ?? "",
      unitModel: intake?.unitModel ?? seedModel ?? "",
      aaVoltage: intake?.aaVoltage ?? "",
      cVoltage: intake?.cVoltage ?? "",
      boardRevision: intake?.boardRevision ?? "",
      visualInspection: intake?.visualInspection ?? "",
    });
    setLookupSerial((intake?.unitSerial ?? seedSerial ?? "").trim());
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [intake, open, onClose, seedModel, seedSerial]);

  useEffect(() => {
    const trimmed = form.unitSerial.trim();
    const handle = window.setTimeout(() => setLookupSerial(trimmed), 250);
    return () => window.clearTimeout(handle);
  }, [form.unitSerial]);

  const modelQuery = useQuery({
    queryKey: ["quality", "rma-intake-model", lookupSerial],
    queryFn: () => fetchDeviceLookup(lookupSerial),
    enabled: open && lookupSerial.length >= 6,
    retry: false,
  });

  useEffect(() => {
    const episode = modelQuery.data?.episodes.find((row) => row.active) ?? modelQuery.data?.episodes[0];
    const sku = episode?.finishedSku?.trim() ?? "";
    const boardRevision = episode?.boardRevision?.trim() ?? "";
    if (sku === "" && boardRevision === "") return;
    setForm((current) => {
      if (current.unitSerial.trim() !== lookupSerial) return current;
      const nextModel = sku !== "" && current.unitModel !== sku ? sku : current.unitModel;
      const nextBoard =
        boardRevision !== "" && current.boardRevision.trim() === "" ? boardRevision : current.boardRevision;
      if (nextModel === current.unitModel && nextBoard === current.boardRevision) return current;
      return { ...current, unitModel: nextModel, boardRevision: nextBoard };
    });
  }, [lookupSerial, modelQuery.data]);

  const logMutation = useMutation({
    mutationFn: () => {
      const input = {
        unitSerial: form.unitSerial.trim() || undefined,
        unitModel: form.unitModel.trim() || undefined,
        visualInspection: form.visualInspection.trim() || undefined,
        aaVoltage: voltageValue(form.aaVoltage, "AA voltage"),
        cVoltage: voltageValue(form.cVoltage, "C voltage"),
        boardRevision: form.boardRevision.trim() || undefined,
      };
      if (intake != null) return updateRmaIntake(rmaId, intake.id, input);
      return addRmaIntake(rmaId, input);
    },
    onSuccess: (detail) => {
      onLogged(detail);
      onClose();
    },
    onError: (error: unknown) => {
      setErrorMessage(error instanceof ApiError ? error.message : "Could not log the received unit.");
    },
  });

  if (!open) return null;

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setErrorMessage(null);
    try {
      voltageValue(form.aaVoltage, "AA voltage");
      voltageValue(form.cVoltage, "C voltage");
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Voltage must be a number such as 3.65.");
      return;
    }
    logMutation.mutate();
  };

  const setField = (key: keyof typeof EMPTY_FORM) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  return createPortal(
    <div {...hubModalOverlayProps(false)} role="dialog" aria-modal="true" aria-labelledby="rma-intake-title">
      <form
        className="my-4 w-full max-w-2xl rounded-lg border border-slate-200 bg-white shadow-xl"
        onSubmit={handleSubmit}
      >
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <h2 id="rma-intake-title" className="text-lg font-semibold text-slate-900">
              {intake != null ? "Intake details" : "Log received unit"}
            </h2>
            <HoverHintWithGlyph hoverHint={MODAL_HELP} size="md" />
          </div>
        </div>

        <div className="space-y-4 px-4 py-4 sm:px-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="rma-intake-serial">Unit serial</Label>
              <Input
                id="rma-intake-serial"
                value={form.unitSerial}
                autoFocus
                onChange={setField("unitSerial")}
              />
            </div>
            <div>
              <Label htmlFor="rma-intake-model">Model</Label>
              <Input
                id="rma-intake-model"
                value={form.unitModel}
                placeholder="Filled from the scanned unit"
                onChange={setField("unitModel")}
              />
            </div>
            <div>
              <Label htmlFor="rma-intake-aa">AA voltage</Label>
              <Input
                id="rma-intake-aa"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                placeholder="3.65"
                value={form.aaVoltage}
                onChange={setField("aaVoltage")}
              />
            </div>
            <div>
              <Label htmlFor="rma-intake-c">C voltage</Label>
              <Input
                id="rma-intake-c"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                placeholder="3.65"
                value={form.cVoltage}
                onChange={setField("cVoltage")}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="rma-intake-board">Board revision</Label>
              <Input id="rma-intake-board" value={form.boardRevision} onChange={setField("boardRevision")} />
            </div>
          </div>
          <div>
            <Label htmlFor="rma-intake-inspection">Visual inspection</Label>
            <TextArea
              id="rma-intake-inspection"
              rows={5}
              className="min-h-32 font-sans"
              value={form.visualInspection}
              onChange={setField("visualInspection")}
            />
          </div>
          {errorMessage ? <p className="text-sm text-rose-700">{errorMessage}</p> : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose} disabled={logMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" isLoading={logMutation.isPending}>
            {intake != null ? "Save details" : "Log received unit"}
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
